import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["active", "aria-invalid", "aria-current", "aria-checked", "autoFocus", "block", "children", "disabled", "external", "iconProps", "loading", "onBlur", "onClick", "onFocus", "onKeyDown", "onKeyUp", "onMouseDown", "pressed", "responsive", "size", "title", "truncate", "truncateStringProps", "truncateTooltip", "use", "type"];
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { getSizeTokens, getUseTokens, isLinkUse, isFormUse, setAsActive } from './Button.helpers.ts';
import { renderChildren, renderExternalButtonIcon, renderTruncatedContents } from './Button.utils';
import styled, { useTheme } from 'foundations-theming/utils';
import { useDisplayOptimizedCssStyles, useResponsiveStyles, useSizeCssObject, useUseVariantCssObject } from './Button.hooks';
import { customizeStylesWithImportant } from '../../../utils/customizeStylesWithImportant';
import emptyFunction from 'react-utils/emptyFunction';
import { filterProps } from '../../../utils/filterProps';
import { mergeProps } from 'react-aria';
import { useColorSwatchContext } from '../ColorSwatch/ColorSwatchProvider';
import { useIconButtonContext } from '../IconButton/IconButtonProvider';
import { useLoadingButtonContext } from '../LoadingButton/LoadingButtonProvider';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { deepMerge } from '../../utils/deepMerge/deepMerge';
import { useAbstractDropdownContext } from '../AbstractDropdown/AbstractDropdownProvider';
import { useDropdownContext } from '../Dropdown/DropdownContext';
import { useFloatingButtonGroupContext } from '../FloatingButtonGroup/FloatingButtonGroupContext';
import { useTabsContext } from '../Tabs/TabsProvider';
import { jsx as _jsx } from "react/jsx-runtime";
export const StyledButton = styled.button.withConfig({
  displayName: "PrivateButton__StyledButton",
  componentId: "sc-1i6o1pf-0"
})(({
  $block,
  $responsiveStyles,
  $sizeCssStyles,
  $baseCssStyles,
  $typography,
  $variantCssStyles,
  $isLinkUse,
  $displayOptimizedCssStyles,
  $styleOverrides,
  $isTransparentUse,
  theme
}) => {
  /**
   * $baseCssStyles: token props set by the user.
   * $styleOverrides: button style overrides from other components.
   * $baseCssStyles should take precedence over $styleOverrides because it is set by the user.
   */
  const baseStylesAndOverrides = deepMerge($styleOverrides, $baseCssStyles);
  return deepMerge(Object.assign({
    cursor: 'pointer',
    transition: `all 150ms ease-out`,
    '-webkit-font-smoothing': $isLinkUse || $isTransparentUse ? 'antialiased' : 'auto',
    '-moz-osx-font-smoothing': $isLinkUse || $isTransparentUse ? 'grayscale' : 'auto'
  }, $variantCssStyles, $sizeCssStyles, theme.typography[$typography], $displayOptimizedCssStyles, $responsiveStyles, $block && {
    width: '100%'
  }, $isLinkUse && {
    fontWeight: 600,
    fontSize: 'inherit',
    lineHeight: 'inherit'
  }, $isTransparentUse && {
    fontWeight: theme.typography[$typography].fontWeight
  }), baseStylesAndOverrides);
});
export const UnstyledButton = styled.button.withConfig({
  displayName: "PrivateButton__UnstyledButton",
  componentId: "sc-1i6o1pf-1"
})(({
  $block,
  $responsiveStyles,
  $baseCssStyles,
  $isLinkUse,
  $styleOverrides
}) => {
  /**
   * $baseCssStyles: token props set by the user.
   * $styleOverrides: button style overrides from other components.
   * $baseCssStyles should take precedence over $styleOverrides because it is set by the user.
   */
  const baseStylesAndOverrides = deepMerge($styleOverrides, $baseCssStyles);
  return deepMerge(Object.assign({
    background: 'none',
    border: 0,
    padding: 0,
    cursor: 'pointer'
  }, $responsiveStyles, $block && {
    width: '100%'
  }, $isLinkUse && {
    fontWeight: 600,
    fontSize: 'inherit',
    lineHeight: 'inherit'
  }), baseStylesAndOverrides);
});
/*
 * We needed to explicitly type this function because Styled Components was having an issue with the union type of the $typography prop.
 * See https://hubspot.slack.com/archives/C4ABAB8CR/p1722362981866609
 */
export const StyledButtonTextContainer = styled.span.withConfig({
  displayName: "PrivateButton__StyledButtonTextContainer",
  componentId: "sc-1i6o1pf-2"
})(({
  $use,
  theme
}) => {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: isLinkUse($use) || isFormUse($use) ? 'flex-start' : 'center',
    boxSizing: 'border-box',
    fontStyle: 'unset',
    textTransform: 'unset',
    backgroundColor: 'unset',
    margin: theme.space['0'],
    padding: theme.space['0'],
    inlineSize: '100%',
    blockSize: '100%',
    color: 'inherit',
    // The color is set on the button element via the variant tokens, so we can just inherit it here
    textAlign: isLinkUse($use) || isFormUse($use) ? 'left' : 'center',
    '& > * + *': {
      marginInlineStart: theme.space['200']
    }
  };
});
export const PrivateButton = /*#__PURE__*/forwardRef((_ref, ref) => {
  var _iconButtonContext$bu, _loadingButtonContext, _abstractDropdownCont, _dropdownContext$butt, _colorSwatchContext$b, _tabsContext$tabsButt;
  let {
      active = false,
      'aria-invalid': ariaInvalid,
      'aria-current': ariaCurrent,
      'aria-checked': ariaChecked,
      autoFocus = false,
      block,
      children,
      disabled = false,
      external,
      iconProps,
      loading = false,
      onBlur = emptyFunction,
      onClick = emptyFunction,
      onFocus = emptyFunction,
      onKeyDown = emptyFunction,
      onKeyUp = emptyFunction,
      onMouseDown = emptyFunction,
      pressed,
      responsive,
      size,
      title,
      truncate,
      truncateStringProps,
      truncateTooltip,
      use,
      type
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    floatingGroup
  } = useFloatingButtonGroupContext();
  const buttonRef = useRef(null);
  const handleClick = e => {
    if (disabled || loading) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick(e);
  };
  const handleFocus = e => {
    onFocus(e);
  };
  const handleBlur = e => {
    onBlur(e);
  };
  const buttonProps = Object.assign({
    onBlur: handleBlur,
    onClick: handleClick,
    onFocus: handleFocus,
    onKeyDown,
    onKeyUp,
    onMouseDown
  }, rest);
  useImperativeHandle(ref, () => buttonRef.current, [buttonRef]);
  const useTokens = getUseTokens(use);
  if (active) {
    setAsActive(useTokens);
  }
  const useVariantCssStyles = useUseVariantCssObject(isLinkUse(use), useTokens);
  const sizeTokens = getSizeTokens(size, use, floatingGroup);
  const sizeCssStyles = useSizeCssObject(sizeTokens);
  const {
    domProps,
    tokenProps,
    callbackProps,
    restProps
  } = filterProps(buttonProps);

  // restProps contains ButtonHTMLAttributes, which need to be passed to the button element
  const mergedProps = mergeProps(domProps, callbackProps, restProps);
  const iconButtonContext = useIconButtonContext();
  const loadingButtonContext = useLoadingButtonContext();
  const abstractDropdownContext = useAbstractDropdownContext();
  const dropdownContext = useDropdownContext();
  const colorSwatchContext = useColorSwatchContext();
  const tabsContext = useTabsContext();
  const theme = useTheme();
  const customizeStyles = customizeStylesWithImportant(/^margin/);
  const baseCssStyles = useMapTokenPropsToCSS(tokenProps, {
    customizeStyles
  });
  const responsiveStyles = useResponsiveStyles(responsive);
  const displayOptimizedCssStyles = useDisplayOptimizedCssStyles();
  const sharedStyleOverrides = deepMerge((_iconButtonContext$bu = iconButtonContext === null || iconButtonContext === void 0 ? void 0 : iconButtonContext.buttonStyleOverrides) !== null && _iconButtonContext$bu !== void 0 ? _iconButtonContext$bu : {}, (_loadingButtonContext = loadingButtonContext === null || loadingButtonContext === void 0 ? void 0 : loadingButtonContext.getButtonStyleOverrides(theme)) !== null && _loadingButtonContext !== void 0 ? _loadingButtonContext : {});
  const newSharedStyleOverrides = deepMerge(sharedStyleOverrides, (_abstractDropdownCont = abstractDropdownContext === null || abstractDropdownContext === void 0 ? void 0 : abstractDropdownContext.buttonStyleOverrides) !== null && _abstractDropdownCont !== void 0 ? _abstractDropdownCont : {});
  const dropdownSharedStyleOverrides = deepMerge(newSharedStyleOverrides, (_dropdownContext$butt = dropdownContext === null || dropdownContext === void 0 ? void 0 : dropdownContext.buttonStyleOverrides) !== null && _dropdownContext$butt !== void 0 ? _dropdownContext$butt : {});
  const newDropdownSharedStyleOverrides = deepMerge(dropdownSharedStyleOverrides, (_colorSwatchContext$b = colorSwatchContext === null || colorSwatchContext === void 0 ? void 0 : colorSwatchContext.buttonStyleOverrides) !== null && _colorSwatchContext$b !== void 0 ? _colorSwatchContext$b : {});
  const privateButtonStyleOverrides = deepMerge(newDropdownSharedStyleOverrides, (_tabsContext$tabsButt = tabsContext === null || tabsContext === void 0 ? void 0 : tabsContext.tabsButtonStyleOverrides) !== null && _tabsContext$tabsButt !== void 0 ? _tabsContext$tabsButt : {});
  const renderedChildren = renderChildren(children);
  const renderedExternalButtonIcon = renderExternalButtonIcon(external, iconProps);
  const renderedContents = truncate ? renderTruncatedContents({
    truncateStringProps,
    truncateTooltip,
    renderedChildren,
    renderedExternalButtonIcon,
    use
  }) : renderedChildren;
  if (use === 'unstyled') {
    return /*#__PURE__*/_jsx(UnstyledButton, Object.assign({}, mergedProps, {
      "aria-disabled": disabled || loading,
      "aria-invalid": ariaInvalid,
      "aria-current": ariaCurrent,
      "aria-checked": ariaChecked,
      "aria-pressed": pressed,
      autoFocus: autoFocus,
      "data-fnd-button": "t",
      ref: buttonRef,
      title: title,
      type: type,
      $block: block,
      $baseCssStyles: baseCssStyles,
      $responsiveStyles: responsiveStyles,
      $isLinkUse: isLinkUse(use),
      $styleOverrides: privateButtonStyleOverrides,
      children: /*#__PURE__*/_jsx(StyledButtonTextContainer, {
        $use: use,
        children: renderedContents
      })
    }));
  }
  return /*#__PURE__*/_jsx(StyledButton, Object.assign({}, mergedProps, {
    "aria-disabled": disabled || loading,
    "aria-invalid": ariaInvalid,
    "aria-current": ariaCurrent,
    "aria-checked": ariaChecked,
    "aria-pressed": pressed,
    autoFocus: autoFocus,
    "data-fnd-button": "t",
    ref: buttonRef,
    title: title,
    type: type,
    $block: block,
    $variantCssStyles: useVariantCssStyles,
    $sizeCssStyles: sizeCssStyles,
    $baseCssStyles: baseCssStyles,
    $typography: sizeTokens.typography,
    $responsiveStyles: responsiveStyles,
    $isLinkUse: isLinkUse(use),
    $displayOptimizedCssStyles: displayOptimizedCssStyles,
    $styleOverrides: dropdownSharedStyleOverrides,
    $isTransparentUse: use === 'transparent',
    children: /*#__PURE__*/_jsx(StyledButtonTextContainer, {
      $use: use,
      children: renderedContents
    })
  }));
});
PrivateButton.displayName = 'PrivateButton';